export interface ProductDetail {
  // Basic identification
  id?: number;
  spuId?: number;

  // SKU information
  skuName?: string;
  skuCode?: string;

  // SPU information
  productName?: string; // maps to spuName
  productCode?: string; // maps to spuCode

  // Category and brand
  productCategories?: string; // maps to categoryName
  productBrand?: string; // maps to brandName

  // Material information
  materialName?: string;

  // Physical dimensions
  productWeight?: number; // maps to weight
  productLength?: number; // maps to length
  productWidth?: number; // maps to width
  productHeight?: number; // maps to height

  // Pricing information (from priceVO)
  costPrice?: number;
  salesPrice?: number;
  marketingPrice?: number; // maps to activityPrice
  marketPrice?: number;
  distributionPrice?: number;
  wholesalePrice?: number;
  settlementPrice?: number; // maps to clearingPrice
  internalApportionmentPrice?: number; // not available in backend

  // Images and content
  mainImage?: string;
  detailedImage?: string; // maps to detailImage
  productDescription?: string; // maps to detailHtml

  // Display and status
  showFlag?: number;
  status?: number; // Backend status (0-4)

  // Audit information
  auditComment?: string; // maps to auditIdea
  submittedBy?: string; // maps to auditCommitName
  auditCommitDate?: string;
  auditCommitBy?: number;

  // Additional fields from backend
  md5CheckValue?: string;

  // Attribute lists
  skuAttributeList?: Array<any>; // AttributeDTO[]
  otherAttributeList?: Array<any>; // AttributeDTO[]

  // Package-specific information
  skuDetailList?: Array<any>; // SkuPackageVO[] for package products
}

// Frontend status values (1-5) - Draft(0) is excluded since Product Review only shows
// SKUs that have been submitted; the numbers are identical to the backend SkuStatus codes.
export type ProductStatus = 1 | 2 | 3 | 4 | 5;

// Backend SkuStatus codes (0-5): 0=Draft, 1=Rejected, 2=Under Review, 3=Effective,
// 4=Effective - Editing (Under Revision), 5=Effective - Rejected (Revision Rejected)
export type BackendStatus = 0 | 1 | 2 | 3 | 4 | 5;

// Status configuration for mapping numeric values to display labels
export interface StatusConfig {
  label: string;
  value: ProductStatus[];
  backendValue: BackendStatus[]; // Backend status values
  severity: 'warning' | 'success' | 'danger' | 'info';
  icon: string;
}

// Status configuration object
export const PRODUCT_STATUS_CONFIG: readonly StatusConfig[] = [
  {
    label: 'Pending Approval',
    value: [2, 4],
    backendValue: [2, 4], // Under Review, Effective - Editing (Under Revision)
    severity: 'warning',
    icon: 'pi pi-clock',
  },
  {
    label: 'Completed',
    value: [3],
    backendValue: [3], // Effective
    severity: 'success',
    icon: 'pi pi-check-circle',
  },
  {
    label: 'Rejected',
    value: [1, 5],
    backendValue: [1, 5], // Rejected, Effective - Rejected (Revision Rejected)
    severity: 'danger',
    icon: 'pi pi-times-circle',
  },
] as const;

// Helper type for status labels
export type StatusLabel = (typeof PRODUCT_STATUS_CONFIG)[number]['label'];

// SPU Type configuration for backend mapping
export interface SpuTypeConfig {
  label: string;
  value: string;
  backendValue: number;
}

export const SPU_TYPE_CONFIG: readonly SpuTypeConfig[] = [
  {
    label: 'Physical Product',
    value: 'Physical Product',
    backendValue: 1,
  },
  {
    label: 'Virtual Product',
    value: 'Virtual Product',
    backendValue: 2,
  },
] as const;

// Helper functions for status operations
export const StatusUtils = {
  /**
   * Get status configuration by numeric status value
   */
  getStatusConfig(status: ProductStatus): StatusConfig | undefined {
    return PRODUCT_STATUS_CONFIG.find((config) =>
      config.value.includes(status)
    );
  },

  /**
   * Get display label for numeric status
   */
  getStatusLabel(status: ProductStatus): string {
    const config = StatusUtils.getStatusConfig(status);
    return config?.label ?? `Status ${status}`;
  },

  /**
   * Get severity for numeric status
   */
  getStatusSeverity(
    status: ProductStatus
  ): 'warning' | 'success' | 'danger' | 'info' {
    const config = StatusUtils.getStatusConfig(status);
    return config?.severity ?? 'info';
  },

  /**
   * Get icon for numeric status
   */
  getStatusIcon(status: ProductStatus): string {
    const config = StatusUtils.getStatusConfig(status);
    return config?.icon ?? 'pi pi-question-circle';
  },

  /**
   * Check if status is pending
   */
  isPending(status: ProductStatus): boolean {
    return [2, 4].includes(status);
  },

  /**
   * Check if status is completed
   */
  isCompleted(status: ProductStatus): boolean {
    return status === 3;
  },

  /**
   * Check if status is rejected
   */
  isRejected(status: ProductStatus): boolean {
    return [1, 5].includes(status);
  },

  /**
   * Get all available status values
   */
  getAllStatusValues(): ProductStatus[] {
    return [1, 2, 3, 4, 5];
  },

  /**
   * Get dropdown options for status filter (for internal use)
   */
  getStatusOptions(): Array<{ label: string; value: ProductStatus[] }> {
    return PRODUCT_STATUS_CONFIG.map((config) => ({
      label: config.label,
      value: config.value,
    }));
  },

  /**
   * Get dropdown options for search form (compatible with FormFieldOption)
   */
  getStatusOptionsForForm(): Array<{ label: string; value: string }> {
    return PRODUCT_STATUS_CONFIG.map((config) => ({
      label: config.label,
      value: config.label, // Use label as value for form compatibility
    }));
  },

  /**
   * Get status values by label (for filtering)
   */
  getStatusValuesByLabel(label: string): ProductStatus[] {
    const config = PRODUCT_STATUS_CONFIG.find((c) => c.label === label);
    return config?.value ?? [];
  },

  /**
   * Get backend status values by frontend status
   */
  getBackendStatusValues(frontendStatus: ProductStatus): BackendStatus[] {
    const config = PRODUCT_STATUS_CONFIG.find((c) =>
      c.value.includes(frontendStatus)
    );
    return config?.backendValue ?? [];
  },

  /**
   * Convert frontend status to backend status. The two numbering schemes are identical
   * (both match SkuStatus's codes) — this only exists so callers don't need to care which
   * "side" a status value came from.
   */
  convertToBackendStatus(frontendStatus: ProductStatus): BackendStatus {
    return frontendStatus;
  },

  /**
   * Convert backend status to frontend status. Backend Draft(0) has no frontend bucket here
   * (Product Review never shows unsubmitted SKUs) — callers should not encounter it in practice.
   */
  convertToFrontendStatus(backendStatus: BackendStatus): ProductStatus {
    return backendStatus as ProductStatus;
  },
} as const;

// SPU Type utilities
export const SpuTypeUtils = {
  /**
   * Get SPU type configuration by label
   */
  getSpuTypeConfig(label: string): SpuTypeConfig | undefined {
    return SPU_TYPE_CONFIG.find((config) => config.label === label);
  },

  /**
   * Get backend value by frontend label
   */
  getBackendValue(label: string): number {
    const config = SpuTypeUtils.getSpuTypeConfig(label);
    return config?.backendValue ?? 1;
  },

  /**
   * Get frontend label by backend value
   */
  getFrontendLabel(backendValue: number): string {
    const config = SPU_TYPE_CONFIG.find((c) => c.backendValue === backendValue);
    return config?.label ?? 'Physical Product';
  },

  /**
   * Get dropdown options for SPU type
   */
  getSpuTypeOptions(): Array<{ label: string; value: string }> {
    return SPU_TYPE_CONFIG.map((config) => ({
      label: config.label,
      value: config.label,
    }));
  },
} as const;

// Updated SKUItem interface to match backend GoodsSkuPageVO structure
export interface SKUItem {
  id?: number;
  spuId?: number;
  spuName?: string;
  spuCode?: string;
  spuType?: number | string;
  categoryCode?: string;
  setFlag?: number; // 0 for Product SKU, 1 for Package SKU
  materialId?: number;
  materialName?: string;
  materialMainImages?: Array<string>;
  materialMainImage?: string; // First image URL for table display
  skuCode?: string;
  skuName?: string;
  categoryName?: string;
  showFlag?: number;
  goodsRemark?: string;
  status?: ProductStatus; // Frontend status (1-5)
  costPrice?: number;
  salesPrice?: number;
  activityPrice?: number;
  marketPrice?: number;
  distributionPrice?: number;
  wholesalePrice?: number;
  clearingPrice?: number;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  auditIdea?: string;
  auditCommitDate?: string;
  skuAttributeList?: Array<any>; // SkuAttributeValue[]
  otherAttributeList?: Array<any>; // SkuAttributeValue[]
}

export interface DropdownOption {
  label: string;
  value: string;
}

export interface TreeNode {
  label: string;
  value: string;
  children?: TreeNode[];
}

export type ProductType = 'ordinary' | 'package';
